import React, { useEffect, useState, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import Title from "../common/Title";
import { FaKey, FaPlus, FaCopy, FaSync } from "react-icons/fa";
import { Button, Form, Row, Col, Badge, Collapse } from "react-bootstrap";
import EnhancedGrid from "../common/EnhancedGrid";
import { mcpTokenActions } from "../../actions/mcpToken.actions";
import { clientActions } from "../../actions/client.actions";
import { clientConstants } from "../../constants/client.constants";
import McpTokenModal from "./McpTokenModal";
import { useToast } from "../../contexts/ToastContext";
import moment from "moment";
import { FaAngleDown, FaAngleUp } from "react-icons/fa";

function McpTokenGrid({ hideTitle }) {
  const dispatch = useDispatch();
  const { showToast } = useToast();
  const mcpTokenState = useSelector((state) => state.mcpToken);
  const clientState = useSelector((state) => state.client);

  const [selectedToken, setSelectedToken] = useState(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [copied, setCopied] = useState(false);
  const [filterExpanded, setFilterExpanded] = useState(false);

  // Filter state
  const [clientIdFilter, setClientIdFilter] = useState("");
  const [isActiveFilter, setIsActiveFilter] = useState("");

  // Create a lookup map for client names
  const clientNameMap = useMemo(() => {
    const map = {};
    (clientState.paginatedResultGrid?.data || []).forEach((client) => {
      map[client.clientId] = client.clientName;
    });
    return map;
  }, [clientState.paginatedResultGrid?.data]);

  useEffect(() => {
    dispatch(mcpTokenActions.get());
    dispatch(clientActions.get({ max_results: 500 }, clientConstants.CALLER_CLIENT_GRID));
  }, [dispatch]);

  // Refresh after CRUD operations
  useEffect(() => {
    if (mcpTokenState.createSuccess || mcpTokenState.updateSuccess) {
      if (mcpTokenState.createSuccess) showToast("Token created successfully", "success");
      if (mcpTokenState.updateSuccess) showToast("Token updated successfully", "success");
      dispatch(mcpTokenActions.get());
      setShowCreateModal(false);
      setShowEditModal(false);
      setSelectedToken(null);
      dispatch(mcpTokenActions.clearSuccess());
    }
    // eslint-disable-next-line
  }, [mcpTokenState.createSuccess, mcpTokenState.updateSuccess]);

  const handleCopyToken = (token) => {
    navigator.clipboard.writeText(token);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleRegenerateToken = () => {
    if (selectedToken) {
      dispatch(mcpTokenActions.update(selectedToken.clientId, { regenerateToken: true }));
    }
  };

  const handleToggleActive = () => {
    if (selectedToken) {
      dispatch(mcpTokenActions.update(selectedToken.clientId, { isActive: !selectedToken.isActive }));
    }
  };

  // Filter tokens
  const filteredTokens = (mcpTokenState.tokens || []).filter((token) => {
    let matches = true;
    if (clientIdFilter) {
      matches = matches && token.clientId?.toLowerCase().includes(clientIdFilter.toLowerCase());
    }
    if (isActiveFilter !== "") {
      const isActiveValue = isActiveFilter === "true";
      matches = matches && token.isActive === isActiveValue;
    }
    return matches;
  });

  const handleClearFilters = () => {
    setClientIdFilter("");
    setIsActiveFilter("");
  };

  return (
    <>
      {!hideTitle && <Title text="MCP Tokens" icon={<FaKey className="mb-1" />} />}

      {/* Filter Section */}
      <div className="bg-light border p-3 mb-3 rounded">
        <Collapse in={filterExpanded}>
          <div>
            <Row className="mb-3">
              <Col md={4}>
                <Form.Group>
                  <Form.Label>Client ID</Form.Label>
                  <Form.Control
                    type="text"
                    value={clientIdFilter}
                    onChange={(e) => setClientIdFilter(e.target.value)}
                    placeholder="Filter by client ID"
                  />
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group>
                  <Form.Label>Status</Form.Label>
                  <Form.Select
                    value={isActiveFilter}
                    onChange={(e) => setIsActiveFilter(e.target.value)}
                  >
                    <option value="">All</option>
                    <option value="true">Active</option>
                    <option value="false">Inactive</option>
                  </Form.Select>
                </Form.Group>
              </Col>
            </Row>
            <Button
              variant="link"
              size="sm"
              className="text-decoration-none p-0 m-0 float-start fw-bold"
              onClick={() => setFilterExpanded(false)}
            >
              Click here to collapse filters <FaAngleUp size={13} />
            </Button>
            <Button
              variant="secondary"
              className="float-end"
              size="sm"
              onClick={handleClearFilters}
            >
              Clear filters
            </Button>
            <div className="clearfix" />
          </div>
        </Collapse>
        {!filterExpanded && (
          <Button
            variant="link"
            size="sm"
            className="text-decoration-none p-0 m-0 fw-bold"
            onClick={() => setFilterExpanded(true)}
          >
            Click here to expand filters <FaAngleDown size={13} />
          </Button>
        )}
      </div>

      {/* Action Buttons */}
      <div className="d-flex gap-2 mb-3">
        <Button
          variant="primary"
          size="sm"
          onClick={() => setShowCreateModal(true)}
        >
          <FaPlus className="mb-1 me-2" />
          Create Token
        </Button>
        {selectedToken && (
          <>
            <Button
              variant="outline-primary"
              size="sm"
              onClick={() => setShowEditModal(true)}
            >
              Edit
            </Button>
            <Button
              variant="outline-secondary"
              size="sm"
              onClick={handleToggleActive}
              disabled={mcpTokenState.updating}
            >
              {selectedToken.isActive ? "Deactivate" : "Activate"}
            </Button>
          </>
        )}
      </div>

      <EnhancedGrid
        gridId="mcptoken"
        fillHeight={hideTitle}
        emptyState={{
          icon: <FaKey size={80} className="text-muted mb-3" />,
          message: "No MCP tokens found",
          subtitle: "Create a token to enable client API authentication.",
          action: (
            <Button variant="primary" size="sm" onClick={() => setShowCreateModal(true)}>
              Create Token
            </Button>
          ),
        }}
        data={filteredTokens.map((token) => ({
          ...token,
          clientName: clientNameMap[token.clientId] || "-",
          status: token.isActive ? (
            <Badge bg="success">Active</Badge>
          ) : (
            <Badge bg="danger">Inactive</Badge>
          ),
          lastUsed: token.lastUsedTimestamp
            ? moment(token.lastUsedTimestamp).format("MM/DD/YYYY h:mm A")
            : "Never",
        }))}
        displayColumns={["clientId", "clientName", "status", "lastUsed"]}
        idColumn={"clientId"}
        selectedRow={selectedToken}
        onRowClick={setSelectedToken}
        pageNumber={0}
        totalPages={filteredTokens.length > 0 ? 1 : 0}
        loading={mcpTokenState.loading}
      />

      {/* Detail Panel */}
      {selectedToken && (
        <div className="mt-3 p-3 bg-light rounded">
          <h5>Token Details</h5>
          <div className="row">
            <div className="col-md-6">
              <p><strong>Client:</strong> {clientNameMap[selectedToken.clientId] || selectedToken.clientId}</p>
              <p><strong>Client ID:</strong> {selectedToken.clientId}</p>
              <p><strong>Client GUID:</strong> {selectedToken.clientGuid}</p>
            </div>
            <div className="col-md-6">
              <p>
                <strong>Status:</strong>{" "}
                {selectedToken.isActive ? (
                  <Badge bg="success">Active</Badge>
                ) : (
                  <Badge bg="danger">Inactive</Badge>
                )}
              </p>
              <p>
                <strong>Last Used:</strong>{" "}
                {selectedToken.lastUsedTimestamp
                  ? moment(selectedToken.lastUsedTimestamp).format("MM/DD/YYYY h:mm A")
                  : "Never"
                }
              </p>
            </div>
          </div>

          {/* Token Value Section */}
          <div className="mt-3 p-3 bg-white rounded border">
            <div className="d-flex align-items-center justify-content-between mb-2">
              <strong>Plain Token:</strong>
              <div className="d-flex gap-2">
                <Button
                  variant="outline-secondary"
                  size="sm"
                  onClick={() => handleCopyToken(selectedToken.plainToken)}
                >
                  <FaCopy className="me-1" />
                  {copied ? "Copied!" : "Copy"}
                </Button>
                <Button
                  variant="outline-warning"
                  size="sm"
                  onClick={handleRegenerateToken}
                  disabled={mcpTokenState.updating}
                >
                  <FaSync className="me-1" />
                  Regenerate
                </Button>
              </div>
            </div>
            <code className="d-block p-2 bg-light rounded" style={{ wordBreak: "break-all" }}>
              {selectedToken.plainToken || <span className="text-muted">Token not available</span>}
            </code>
            <small className="text-muted">
              This token is used for API authentication. Keep it secure and do not share it publicly.
            </small>
          </div>
        </div>
      )}

      {/* Create Modal */}
      <McpTokenModal
        show={showCreateModal}
        onHide={() => setShowCreateModal(false)}
        mode="create"
      />

      {/* Edit Modal */}
      <McpTokenModal
        show={showEditModal}
        onHide={() => setShowEditModal(false)}
        mode="edit"
        token={selectedToken}
      />
    </>
  );
}

export default McpTokenGrid;
